import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    CraftDto,
    CraftService,
    FlyFreelyError,
    FlyFreelyLoggingService,
    OrganisationRelationshipDto,
    OrganisationRelationshipService,
    PersonsOrganisationDto,
    RpaTypeDto,
    ShareResourceCommand,
    UnshareResourceCommand,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { Angulartics2 } from 'angulartics2';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { OrganisationRelationshipsUiService } from './organisation-relationships-ui.service';

@Component({
    selector: 'shared-rpa',
    styleUrls: ['./common.scss'],
    template: `<h5 [ngClass]="{ readonly: working }">Shared RPAs</h5>
        <div class="input-group">
            <ng-select
                [items]="
                    organisationRpaList$
                        | async
                        | sortBy: 'nickname'
                        | filterOutById: relationship.sharedRpaList
                "
                bindValue="id"
                bindLabel="nickname"
                appendTo="body"
                [formControl]="addRpa"
                placeholder="Select RPA to share with {{
                    relationship.childOrganisation.name
                }}"
                [readonly]="working || isReadOnlyAdmin"
                [clearable]="false"
            >
                <ng-template ng-option-tmp let-item="item" let-index="index">
                    <rpa-list-item
                        [item]="item"
                        [showCustodian]="true"
                    ></rpa-list-item>
                </ng-template>
                <ng-template ng-label-tmp let-item="item" let-index="index">
                    <status-icon [rpa]="item" [clickable]="false"></status-icon>
                </ng-template>
            </ng-select>
            <span class="input-group-btn">
                <button
                    type="button"
                    class="btn btn-primary"
                    [disabled]="working || !addRpa.valid || isReadOnlyAdmin"
                    (click)="shareRpa()"
                >
                    Share
                </button>
            </span>
        </div>
        <div>
            <div
                *ngFor="let rpa of relationship.sharedRpaList | sortBy: 'nickname'"
                class="top-buffer horizontal-container center-children"
            >
                <rpa-list-item
                    class="shared-item"
                    [item]="rpa"
                    [showTypeRequired]="rpaRequiringSharedTypeLookup[rpa.id]"
                    [showCustodian]="true"
                ></rpa-list-item>

                <button
                    *ngIf="!isReadOnlyAdmin"
                    type="button"
                    class="btn btn-tertiary btn-delete"
                    tooltip="Remove sharing of RPA"
                    placement="top"
                    (click)="unshareRpa(rpa)"
                >
                    <span class="fal fa-trash-alt"></span>
                </button>

                <div>
                    <button
                        *ngIf="rpa.custodian == null"
                        type="button"
                        class="btn btn-xs btn-primary"
                        tooltip="Set this organisation as the custodian"
                        placement="top"
                        [disabled]="working || isReadOnlyAdmin"
                        (click)="updateCustodian(rpa.id)"
                    >
                        Set as Custodian
                    </button>
                    <button
                        *ngIf="rpa.custodian != null"
                        type="button"
                        class="btn btn-xs btn-primary"
                        tooltip="Set custodian back to the owner"
                        placement="top"
                        [disabled]="working || isReadOnlyAdmin"
                        (click)="clearCustodian(rpa.id)"
                    >
                        Clear Custodian
                    </button>
                    <button
                        *ngIf="
                            rpa.maintenanceOrganisation.id !==
                            relationship.childOrganisation.id
                        "
                        type="button"
                        class="btn btn-xs btn-primary"
                        tooltip="Set this organisation as the maintenance organisation"
                        placement="top"
                        [disabled]="working || isReadOnlyAdmin"
                        (click)="updateMaintenanceOrganisation(rpa.id)"
                    >
                        Set as Maintenance Organisation
                    </button>
                    <button
                        *ngIf="
                            rpa.maintenanceOrganisation.id ===
                            relationship.childOrganisation.id
                        "
                        type="button"
                        class="btn btn-xs btn-primary"
                        tooltip="Set maintenance organisation back to the owner"
                        placement="top"
                        [disabled]="working || isReadOnlyAdmin"
                        (click)="cleanMaintenanceOrganisation(rpa)"
                    >
                        Clear as Maintenance Organisation
                    </button>
                </div>
            </div>
        </div>`
})
export class SharedRpa implements OnInit, OnDestroy {
    @Input() organisation: PersonsOrganisationDto;
    @Input() relationship: OrganisationRelationshipDto;
    @Input() isReadOnlyAdmin = false;

    working = false;
    private ngUnsubscribe$ = new Subject<void>();

    organisationRpaList$: Observable<CraftDto[]>;

    rpaRequiringSharedTypeLookup: { [rpaId: number]: RpaTypeDto } = {};

    addRpa = new FormControl<number>(undefined, [Validators.required]);

    constructor(
        public workTracker: WorkTracker,
        private logging: FlyFreelyLoggingService,
        private angulartics2: Angulartics2,
        private organisationRelationshipService: OrganisationRelationshipService,
        private organisationRelationshipsUi: OrganisationRelationshipsUiService,
        private rpaService: CraftService,
        private commonDialoguesService: CommonDialoguesService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit(): void {
        this.setupOrganisationRpaList();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    setupOrganisationRpaList() {
        this.organisationRpaList$ =
            this.organisationRelationshipsUi.organisationRpaList$.pipe(
                map(res =>
                    res.filter(
                        item => item.organisationId === this.organisation.id
                    )
                ),
                takeUntil(this.ngUnsubscribe$)
            );

        this.organisationRpaList$
            .pipe(
                switchMap(rpa =>
                    combineLatest(
                        rpa.map(r =>
                            this.organisationRelationshipsUi
                                .findRpaTypeToShare(r.id, this.relationship.id)
                                .pipe(
                                    map(rpaType => ({
                                        ...r,
                                        requiresType: rpaType
                                    }))
                                )
                        )
                    )
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(rpaWithTypeDependencies =>
                rpaWithTypeDependencies.forEach(
                    rpa =>
                        (this.rpaRequiringSharedTypeLookup[rpa.id] =
                            rpa.requiresType)
                )
            );
    }

    shareRpa() {
        this.organisationRelationshipsUi
            .findRpaTypeToShare(this.addRpa.value, this.relationship.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(rpaType => {
                if (rpaType == null) {
                    this.shareRpaToChild();
                } else {
                    this.commonDialoguesService
                        .showConfirmationDialogue(
                            'Sharing RPA with custom type',
                            `The RPA you are sharing is of the custom RPA type <em>${rpaType.make} ${rpaType.model}</em>. Clicking OK will share this custom RPA type with ${this.relationship.childOrganisation?.name}.`,
                            'OK',
                            () => Promise.resolve(this.shareRpaType(rpaType))
                        )
                        .catch(() =>
                            this.logging.warn(
                                `The RPA was not shared with ${this.relationship.childOrganisation?.name} due to the required RPA type not being shared.`
                            )
                        );
                }
            });
    }

    shareRpaType(rpaType: RpaTypeDto) {
        const cmd: ShareResourceCommand = {
            resourceId: rpaType.id
        };
        this.organisationRelationshipService
            .shareRpaType(this.organisation.id, this.relationship.id, cmd)
            .subscribe({
                next: result => {
                    this.angulartics2.eventTrack.next({
                        action: 'share-rpa-type',
                        properties: {
                            category: 'shared-resources',
                            label: result.id
                        }
                    });
                    this.shareRpaToChild();
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while sharing RPA Type: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    shareRpaToChild() {
        const cmd: ShareResourceCommand = {
            resourceId: this.addRpa.value
        };
        this.organisationRelationshipService
            .shareRpa(this.organisation.id, this.relationship.id, cmd)
            .subscribe(results => {
                this.addRpa.setValue(undefined);
                this.angulartics2.eventTrack.next({
                    action: 'share-rpa',
                    properties: {
                        category: 'shared-resources',
                        label: results.id
                    }
                });
            })
            .add(this.workTracker.createTracker());
    }

    unshareRpa(rpa: CraftDto) {
        const cmd: UnshareResourceCommand = { resourceId: rpa.id };
        this.organisationRelationshipService
            .unshareRpa(this.organisation.id, this.relationship.id, cmd)
            .subscribe(result => {
                this.angulartics2.eventTrack.next({
                    action: 'unshare-rpa',
                    properties: {
                        category: 'shared-resources',
                        label: result.id
                    }
                });
            })
            .add(this.workTracker.createTracker());
    }

    updateCustodian(rpaId: number) {
        this.rpaService
            .updateCustodian(rpaId, {
                managingOrganisationId: this.organisation.id,
                custodianOrganisationId: this.relationship.childOrganisation.id
            })
            .subscribe({
                next: () =>
                    this.logging.success(
                        `Custodian set to ${this.relationship.childOrganisation.name}`
                    ),
                error: error =>
                    this.logging.error(
                        error,
                        'Error while updating RPA custodian'
                    )
            })
            .add(this.workTracker.createTracker());
    }

    clearCustodian(rpaId: number) {
        this.rpaService
            .updateCustodian(rpaId, {
                managingOrganisationId: this.organisation.id,
                custodianOrganisationId: null
            })
            .subscribe({
                next: () => this.logging.success('Custodian set back to owner'),
                error: error =>
                    this.logging.error(
                        error,
                        'Error while updating RPA custodian'
                    )
            })
            .add(this.workTracker.createTracker());
    }

    updateMaintenanceOrganisation(rpaId: number) {
        this.rpaService
            .updateMaintenanceOrganisation(rpaId, {
                managingOrganisationId: this.organisation.id,
                maintenanceOrganisationId:
                    this.relationship.childOrganisation.id
            })
            .subscribe({
                next: () =>
                    this.logging.success(
                        `Maintenance organisation set to ${this.relationship.childOrganisation.name}`
                    ),
                error: error =>
                    this.logging.error(
                        error,
                        'Error while updating RPA maintenance organisation'
                    )
            })
            .add(this.workTracker.createTracker());
    }

    cleanMaintenanceOrganisation(rpa: CraftDto) {
        this.rpaService
            .updateMaintenanceOrganisation(rpa.id, {
                managingOrganisationId: this.organisation.id,
                maintenanceOrganisationId: rpa.organisationId
            })
            .subscribe({
                next: () =>
                    this.logging.success(
                        'Maintenance organisation set back to owner'
                    ),
                error: error =>
                    this.logging.error(
                        error,
                        'Error while updating RPA maintenance organisation'
                    )
            })
            .add(this.workTracker.createTracker());
    }
}
